// Filing a run from Claims → Runs: the CSV, the 837P, "Mark uploaded",
// acknowledgements, the SFTP transport and the remittances it fetches.
//
// Moved out of pages/finance/BillingRun.js on 2026-10-20. The rules and writes
// are lib/billing/filing.js; this holds the screen's state around them.

import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { identityFromCompanyInfo } from '../../../lib/edi/billingIdentity';
import { loadCompanyInfoRows } from '../../../lib/edi/companyInfo';
import {
  csvFileName, loadRunCsv, loadSubmissions as fetchSubmissions, generate837P,
  markSubmissionSent, queueForSftp, recordAcknowledgement,
} from '../../../lib/billing/filing';
import { destinationLabel, filesWithGammis } from '../../../lib/serviceModel';

/**
 * @param {object[]} p.runs       the run list, for the destination of each run
 * @param {object}   p.activeRun  the expanded run, whose lines are reloaded after a change
 * @param {object}   p.profile    the signed-in user
 * @param {Function} p.loadRuns
 * @param {Function} p.loadLines  (runId)
 */
// What an acknowledgement's problems are under. They used to appear as "File
// exported, with warnings", which reads as good news for a rejected file.
const acknowledgementHeading = (result) =>
  result.update?.status === 'rejected' ? (result.kind === 'TA1' ? 'GAMMIS rejected the file\'s envelope' : 'GAMMIS rejected this file')
  : result.update?.status === 'partially_accepted' ? 'GAMMIS accepted part of this file'
  : 'GAMMIS acknowledged this file, with notes';

export default function useFiling({ runs, activeRun, profile, loadRuns, loadLines }) {
  // ── NEW: Company info cache ───────────────────────────────────────────────
  const [identity, setIdentity] = useState(null);
  // The same rows the identity is derived from, kept because the transport
  // settings live beside it: the two SFTP directories and the two dates
  // without which the credentials expire without anyone being told.
  const [settings, setSettings] = useState({});
  const [transfers, setTransfers] = useState([]);
  // Remittances received but not yet matched against claim lines. Before the
  // SFTP worker, every remittance was posted in the same breath as it was
  // read, so this list was always empty. It is not any more.
  const [pendingRemits, setPendingRemits] = useState([]);
  const [transport, setTransport] = useState(null);   // { busy } | { error } | { result }
  const [exportProblems, setExportProblems] = useState(null);

  // Load company info once: the 837P identity and the transport settings come
  // out of the same rows, so they are read together rather than twice.
  useEffect(() => {
    loadCompanyInfoRows().then((rows) => {
      setIdentity(identityFromCompanyInfo(rows));
      setSettings(Object.fromEntries(rows.map(r => [r.label, (r.value || '').trim()])));
    }).catch(() => setIdentity(null));
  }, []);
  // ── GAMMIS CSV Export ─────────────────────────────────────────────────────
  const exportGAMMIS = async (runId, dateFromStr, dateToStr) => {
    const csv = await loadRunCsv(supabase, runId);
    if (!csv) return;
    const dest = destinationLabel(runs.find(r => r.id === runId)?.payer);
    const a = document.createElement('a');
    a.href = 'data:text/csv,' + encodeURIComponent(csv);
    a.download = csvFileName(dest, dateFromStr, dateToStr);
    a.click();
  };

  // ── 837P: generate, record, file ─────────────────────────────────────────
  // Pre-flight runs first and blocks rather than warning. Everything it checks
  // produces a claim that is rejected or paid wrong, and a file that has left
  // the building is harder to take back than a button that refuses.
  const [submissions, setSubmissions] = useState({});
  const [submitting, setSubmitting] = useState(null);
  const ackFileRef = useRef(null);
  // Which submission an acknowledgement belongs to. Held in a ref as well as
  // state: the change event can in principle arrive before React has
  // re-rendered with the new state, and the handler would then return silently
  // having done nothing — the one outcome this flow must never produce.
  const [ackFor, setAckFor] = useState(null);
  const ackForRef = useRef(null);
  const chooseAckFor = (sub) => { ackForRef.current = sub; setAckFor(sub); };

  const loadSubmissions = useCallback(async (runId) => {
    const data = await fetchSubmissions(supabase, runId);
    setSubmissions(p => ({ ...p, [runId]: data }));
  }, []);

  const downloadEdi = (edi, fileName) => {
    const a = document.createElement('a');
    a.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent(edi);
    a.download = fileName;
    a.click();
  };

  const exportEDI = async (runId, dateFromStr, dateToStr) => {
    setExportProblems(null);
    const run = runs.find(r => r.id === runId);
    // THE GUARD. Only a GAMMIS run gets an 837P. A CareSource run is filed
    // on CareSource's side; building a GAMMIS-addressed file for it would be
    // wrong every time and, with the transport live, one click from sent.
    if (!filesWithGammis(run)) {
      setExportProblems({ runId, blocking: [`This is a ${destinationLabel(run?.payer)} run. It is filed with ${destinationLabel(run?.payer)} outside CAERA — export the CSV, file it there, and record the claim number here.`], warnings: [] });
      return;
    }
    setSubmitting(runId);
    try {
      // Pre-flight, build, record the submission and the control numbers —
      // lib/billing/filing.js. It refuses rather than warns.
      const result = await generate837P(supabase, {
        runId, run, identity, dateFrom: dateFromStr, dateTo: dateToStr, profile,
      });
      if (result.blocking) {
        setExportProblems({ runId, blocking: result.blocking, warnings: result.warnings });
        return;
      }
      downloadEdi(result.edi, result.fileName);
      await loadSubmissions(runId);
      if (result.warnings.length) setExportProblems({ runId, blocking: [], warnings: result.warnings, exported: true });
    } catch (err) {
      setExportProblems({ runId, blocking: [err.message], warnings: [] });
    } finally {
      setSubmitting(null);
    }
  };

  // "Sent" is a real state, not an assumption. A file generated and never
  // uploaded is exactly the failure this flag exists to make visible.
  const markSent = async (sub) => {
    try {
      await markSubmissionSent(supabase, { sub, profile, now: new Date().toISOString() });
    } catch (err) {
      setExportProblems({ runId: sub.billing_run_id, blocking: [err.message], warnings: [] });
    }
    await loadSubmissions(sub.billing_run_id);
    loadRuns();
    if (activeRun?.id === sub.billing_run_id) loadLines(sub.billing_run_id);
  };

  // ── Automatic transport ─────────────────────────────────────────────────
  //
  // Everything above this line assumes a person in the middle: generate,
  // download, upload in the portal, come back tomorrow, download the answer,
  // drop it in. These two call the SFTP worker instead, which does the same
  // journey without the person — and, crucially, records every step, so that a
  // week that did not go is visible rather than merely absent.
  //
  // The worker is only reachable through the edge function, which holds the
  // key. Nothing here ever sees it.

  const transportConfigured = Boolean(settings['SFTP Outbound Directory'] && settings['SFTP Inbound Directory']);

  const loadPendingRemittances = useCallback(async () => {
    const { data } = await supabase.from('remittances')
      .select('id, billing_run_id, source, trace_number, payment_date, payment_amount, payer_name, file_name, raw, imported_at')
      .is('posted_at', null).order('imported_at', { ascending: false });
    setPendingRemits(data || []);
  }, []);

  useEffect(() => { loadPendingRemittances(); }, [loadPendingRemittances]);

  const loadTransfers = useCallback(async () => {
    const { data } = await supabase.from('edi_transfers')
      .select('*').order('started_at', { ascending: false }).limit(20);
    setTransfers(data || []);
  }, []);

  useEffect(() => { if (transportConfigured) loadTransfers(); }, [transportConfigured, loadTransfers]);

  // Two unhelpful messages from the Supabase client, both on the one screen
  // where somebody is trying to get this configured.
  //
  // "Failed to send a request to the Edge Function" means the function is not
  // deployed, and reads like a network blip.
  //
  // "Edge Function returned a non-2xx status code" means the function answered
  // and the client did not read the answer — it puts the body in
  // error.context instead. That body is the whole message: the 503 names the
  // exact secret or setting that is missing. Swallowing it leaves the reader
  // with nothing to act on.
  const readInvokeError = async (error) => {
    const raw = error?.message || 'The transfer service failed.';
    if (/Failed to send a request/i.test(raw)) {
      return 'Could not reach the transfer service. Check that the edi-transport function is deployed.';
    }
    try {
      const body = await error?.context?.json?.();
      if (body?.error) return body.error;
    } catch { /* not JSON, or already read */ }
    return raw;
  };

  const callTransport = async (body, describe) => {
    setTransport({ busy: describe });
    try {
      const { data, error } = await supabase.functions.invoke('edi-transport', { body });
      if (error) throw new Error(await readInvokeError(error));
      if (data?.error) throw new Error(data.error);
      setTransport({ result: data });
      await loadTransfers();
      return data;
    } catch (err) {
      setTransport({ error: err.message });
      return null;
    }
  };

  // Queue for SFTP, then send. The channel is set here rather than at
  // generation because generating a file is not the same as deciding to file
  // it: a run can be exported, read, and thought better of. Nothing leaves
  // until somebody presses this.
  // The Send button only asks; SendToGammisDialog shows what is about to leave
  // and calls confirmSend. Nothing is queued until then.
  const [sendConfirm, setSendConfirm] = useState(null);
  const requestSend = (sub) => setSendConfirm(sub);
  const confirmSend = async () => {
    const sub = sendConfirm;
    if (!sub) return;
    await sendToGammis(sub);
    setSendConfirm(null);
  };

  const sendToGammis = async (sub) => {
    // Belt and braces: a submission can only exist for a GAMMIS run, but the
    // transport is the one place a wrong file cannot be taken back from.
    if (!filesWithGammis(runs.find(r => r.id === sub.billing_run_id))) {
      setTransport({ error: 'This run does not file with GAMMIS and cannot be sent over SFTP.' });
      return;
    }
    const { error } = await queueForSftp(supabase, sub.id);
    if (error) { setTransport({ error: `Could not queue the file: ${error.message}` }); return; }

    const data = await callTransport(
      { mode: 'push', submissionId: sub.id },
      `Sending ${sub.file_name} to GAMMIS`,
    );
    const failure = data?.push?.failed?.find(f => f.submission === sub.id);
    if (failure) setTransport({ error: failure.error });

    await loadSubmissions(sub.billing_run_id);
    loadRuns();
    if (activeRun?.id === sub.billing_run_id) loadLines(sub.billing_run_id);
  };

  // Ask GAMMIS what happened to every claim still waiting on an answer. This
  // is what ends manual ICN entry: the 277 comes back keyed on the control
  // number we sent, so the ICN lands on the right line without anyone reading
  // it off a screen and typing it.
  const askClaimStatus = async () => {
    const data = await callTransport({ mode: 'status' }, 'Asking GAMMIS about outstanding claims');
    if (!data) return;
    loadRuns();
    if (activeRun?.id) loadLines(activeRun.id);
  };

  // Ask what Gainwell has left for us. Safe to press as often as you like: a
  // file already recorded is not fetched again.
  const refreshFromGammis = async () => {
    const data = await callTransport({ mode: 'pull' }, 'Checking GAMMIS for new files');
    if (!data) return;
    await loadPendingRemittances();
    for (const runId of Object.keys(submissions)) await loadSubmissions(runId);
    loadRuns();
    if (activeRun?.id) loadLines(activeRun.id);
  };

  const handleAckFile = (e) => {
    const file = e.target.files?.[0];
    const sub = ackForRef.current || ackFor;
    e.target.value = '';
    if (!file || !sub) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      let result;
      try {
        result = await recordAcknowledgement(supabase, { sub, text: String(ev.target.result) });
      } catch (err) {
        result = { ok: false, error: err.message };
      }
      if (!result.ok) {
        setExportProblems({ runId: sub.billing_run_id, blocking: [result.error], warnings: [] });
        chooseAckFor(null);
        return;
      }
      await loadSubmissions(sub.billing_run_id);
      loadRuns();
      if (activeRun?.id === sub.billing_run_id) loadLines(sub.billing_run_id);
      if (result.problems?.length) {
        setExportProblems({ runId: sub.billing_run_id, blocking: [], warnings: result.problems, heading: acknowledgementHeading(result) });
      }
      chooseAckFor(null);
    };
    reader.readAsText(file);
  };

  return {
    identity, settings, transfers, pendingRemits, transport, exportProblems, setExportProblems,
    submissions, submitting, ackFileRef, chooseAckFor, loadSubmissions, loadPendingRemittances,
    downloadEdi, exportGAMMIS, exportEDI, markSent, transportConfigured,
    requestSend, sendConfirm, setSendConfirm, confirmSend,
    askClaimStatus, refreshFromGammis, handleAckFile,
  };
}
